import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Check, Heart, ArrowLeft, Loader2, Truck, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { CURRENCIES } from "@/i18n";
import { fetchProduct } from "@/api";
import { recordView } from "@/lib/browsingHistory";
import Seo, { SITE_URL } from "@/components/Seo";
import { SizeGuide } from "@/components/SizeGuide";
import { PriceDisplay } from "@/components/PriceDisplay";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const COLOR_HEX = {
  Black: "#111111", White: "#F5F5F0", Gold: "#C4A24A", Silver: "#C0C0C0", Beige: "#D8C5A5",
  Brown: "#6B4A2B", Red: "#8E1B1B", Blue: "#1E3A5F", Green: "#2F4F3E", Pink: "#E6B7C1",
};

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currency, addToCart, inCart, toggleWishlist, inWishlist, t, lang } = useApp();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    fetchProduct(id).then((p) => { setProduct(p); setActive(0); setSelectedSize(null); recordView(p); })
      .catch(() => setProduct(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-[70vh] flex items-center justify-center pt-32"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;
  if (!product) return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center pt-32 gap-4" data-testid="product-not-found">
      <p className="font-display text-3xl">Piece not found</p>
      <button onClick={() => navigate("/catalogue")} className="text-xs uppercase tracking-[0.2em] link-underline">Back to catalogue</button>
    </div>
  );

  const gallery = (product.images && product.images.length ? product.images : [product.image]).filter(Boolean);
  const subtitle = lang === "ru" ? product.subtitle_ru : product.subtitle_en;
  const stock = product.stock ?? 0;
  const sizes = product.sizes || [];
  const sizeStock = product.size_stock || {};
  const tracksSizes = Object.keys(sizeStock).length > 0;
  const hasSizes = sizes.length > 0;
  const allSizesGone = tracksSizes && sizes.length > 0 && sizes.every((s) => (sizeStock[s] || 0) <= 0);
  const soldOut = stock <= 0 || allSizesGone;
  const added = inCart(product.id);
  const liked = inWishlist(product.id);
  const sizeAvailable = (s) => (tracksSizes ? (sizeStock[s] || 0) > 0 : stock > 0);

  const currencyMeta = CURRENCIES[currency] || CURRENCIES.USD;
  const priceNumber = (Math.round(product.price_rub * currencyMeta.rate * 100) / 100).toFixed(2);
  const productUrl = `${SITE_URL}/product/${product.id}`;
  const brandName = (product.brand || "").trim();
  const nameWithBrand = brandName ? `${brandName} ${product.name}` : product.name;
  const altText = `${nameWithBrand} – BuyHeda`;

  // Dynamic, localized SEO title + description built from real product data.
  const seoTitle = nameWithBrand;
  const p = t.seo.product;
  const seoDescription = `${p.lead} ${nameWithBrand} ${p.tail}${subtitle ? " " + subtitle + "." : ""} ${p.shipping}`;

  const productLd = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: product.name,
    ...(brandName ? { brand: { "@type": "Brand", name: brandName } } : {}),
    ...(subtitle ? { description: subtitle } : {}),
    ...(gallery.length ? { image: gallery } : {}),
    sku: product.id,
    productID: product.id,
    url: productUrl,
    offers: {
      "@type": "Offer",
      url: productUrl,
      priceCurrency: currency,
      price: priceNumber,
      availability: soldOut ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
    },
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "BuyHeda", item: SITE_URL + "/" },
      { "@type": "ListItem", position: 2, name: t.nav.catalogue, item: SITE_URL + "/catalogue" },
      ...(product.category
        ? [{ "@type": "ListItem", position: 3, name: (t.shop.filters[product.category] || product.category), item: `${SITE_URL}/catalogue?category=${product.category}` }]
        : []),
      { "@type": "ListItem", position: product.category ? 4 : 3, name: product.name, item: productUrl },
    ],
  };

  const handleAdd = () => {
    if (hasSizes && !selectedSize) { toast.error(t.shop.chooseSize); return; }
    addToCart(product, hasSizes ? selectedSize : null);
  };

  return (
    <div className="pt-28 md:pt-32 pb-24 px-5 md:px-10" data-testid={`product-detail-${product.id}`}>
      <Seo
        title={seoTitle}
        description={seoDescription}
        image={(gallery && gallery[0]) || undefined}
        type="product"
        canonicalPath={`/product/${product.id}`}
        jsonLd={[productLd, breadcrumbLd]}
      />
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted hover:text-ink transition-colors mb-8" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Gallery */}
        <div className="flex flex-col-reverse sm:flex-row gap-4">
          {gallery.length > 1 && (
            <div className="flex sm:flex-col gap-3">
              {gallery.map((img, i) => (
                <button key={i} onClick={() => setActive(i)} data-testid={`thumb-${i}`}
                  className={`h-16 w-14 sm:h-20 sm:w-16 overflow-hidden bg-surface border transition-colors ${active === i ? "border-ink" : "border-transparent hover:border-line"}`}>
                  <img src={img} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <div className="flex-1 relative overflow-hidden bg-surface aspect-[3/4]">
            <motion.img key={active} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
              src={gallery[active]} alt={altText} className={`h-full w-full object-cover ${soldOut ? "opacity-70 grayscale-[0.35]" : ""}`} data-testid="detail-main-image" />
            {soldOut && <span className="absolute top-4 left-4 bg-ink text-paper text-[10px] uppercase tracking-[0.18em] px-2.5 py-1">{t.shop.soldOut}</span>}
          </div>
        </div>

        {/* Info */}
        <div className="lg:pt-6">
          <p className="text-[11px] uppercase tracking-[0.24em] text-muted mb-2">{product.brand}</p>
          <h1 className="font-display font-light text-4xl md:text-5xl leading-none mb-4">{product.name}</h1>
          <p className="text-base text-ink/60 font-light mb-6">{subtitle}</p>
          <div className="mb-8">
            <p className="font-sans text-2xl">
              <PriceDisplay priceRub={product.price_rub} originalRub={product.original_price_rub} currency={currency} testId="detail-price" priceClassName="font-medium" originalClassName="text-xl" />
            </p>
            <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-ink/50" data-testid="shipping-included-note">
              <Truck className="h-3.5 w-3.5" strokeWidth={1.5} /> {t.shop.shippingIncluded}
            </p>
          </div>

          {(product.colors || []).length > 0 && (
            <div className="mb-6">
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted mb-2">Colours</p>
              <div className="flex items-center gap-2">
                {product.colors.map((c) => (
                  <span key={c} title={c} className="h-5 w-5 rounded-full border border-black/15" style={{ backgroundColor: COLOR_HEX[c] || "#ccc" }} />
                ))}
              </div>
            </div>
          )}

          {hasSizes && !soldOut && (
            <div className="mb-8" data-testid="detail-size-picker">
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted">
                  {product.category === "shoes" ? `${t.shop.selectSize} (EU)` : product.category === "jewellery" ? `${t.shop.selectSize} (RU)` : t.shop.selectSize}
                </p>
                {product.category === "jewellery" && <SizeGuide type="ring" />}
                {product.category === "shoes" && <SizeGuide type="shoe" />}
                {product.category === "clothing" && <SizeGuide type="clothing" />}
              </div>
              {(() => {
                const CLOTHING_ORDER = { XS: 0, S: 1, M: 2, L: 3, XL: 4, XXL: 5 };
                const prefix = product.category === "shoes" ? "EU " : product.category === "jewellery" ? "RU " : "";
                const ordered = [...sizes].sort((a, b) =>
                  product.category === "clothing"
                    ? (CLOTHING_ORDER[a] ?? 99) - (CLOTHING_ORDER[b] ?? 99)
                    : parseFloat(String(a).replace("½", ".5")) - parseFloat(String(b).replace("½", ".5"))
                );
                return (
                  <div className="max-w-[16rem]">
                    <Select value={selectedSize || ""} onValueChange={(v) => setSelectedSize(v)}>
                      <SelectTrigger data-testid="detail-size-select" className="w-full bg-transparent border-line uppercase tracking-[0.08em] text-xs">
                        <SelectValue placeholder={t.shop.selectSize} />
                      </SelectTrigger>
                      <SelectContent className="bg-paper border-line max-h-72">
                        {ordered.map((s) => (
                          <SelectItem key={s} value={s} disabled={!sizeAvailable(s)} data-testid={`detail-size-opt-${s.toLowerCase()}`}>
                            {prefix}{s}{!sizeAvailable(s) ? ` — ${t.shop.soldOut}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })()}
            </div>
          )}

          <div className="flex items-center gap-3">
            {soldOut ? (
              <div className="flex-1 text-center bg-ink/85 text-paper py-4 text-xs uppercase tracking-[0.2em]" data-testid="detail-soldout">{t.shop.soldOut}</div>
            ) : (
              <button onClick={handleAdd} data-testid="detail-add-to-cart"
                className={`flex-1 flex items-center justify-center gap-2 py-4 text-xs uppercase tracking-[0.2em] transition-colors duration-500 ${added ? "bg-accent text-ink" : "bg-ink text-paper hover:bg-accent hover:text-ink"}`}>
                {added ? <><Check className="h-4 w-4" /> {t.shop.added}</> : <><Plus className="h-4 w-4" /> {t.shop.add}</>}
              </button>
            )}
            <button onClick={() => toggleWishlist(product)} data-testid="detail-wishlist"
              className={`h-[52px] w-[52px] flex items-center justify-center border transition-colors ${liked ? "bg-ink text-paper border-ink" : "border-line hover:border-ink"}`}>
              <Heart className="h-5 w-5" strokeWidth={1.4} fill={liked ? "currentColor" : "none"} />
            </button>
          </div>

          {/* Price Match Guarantee */}
          <div className="mt-6 border-t border-line pt-5" data-testid="price-match-guarantee">
            <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-ink" data-testid="price-match-badge">
              <BadgeCheck className="h-4 w-4 text-accent" strokeWidth={1.5} /> {t.shop.priceMatch.badge}
            </p>
            <p className="mt-2 text-xs text-ink/55 font-light leading-relaxed" data-testid="price-match-text">{t.shop.priceMatch.text}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
